import { useCallback, useState } from "react";

import type {
  ColumnMappingRecord,
  DataVaultModel,
  HubEntity,
  LinkEntity,
  SatelliteEntity,
  TableRecord
} from "../model/types";
import { getConstructTypeForState } from "../model/constructs";
import { nextLinkNaming } from "../../../helpers/linkNaming";

export type WorkspaceModelHook = ReturnType<typeof useWorkspaceModel>;

export function useWorkspaceModel(
  showStatus: (message: string) => void,
  options?: { defaultDwTargetType?: string | null }
) {
  const [model, setModel] = useState<DataVaultModel | null>(null);
  const defaultDwTargetType = options?.defaultDwTargetType ?? null;

  const addHub = useCallback((): string | null => {
    let createdId: string | null = null;
    setModel((previous) => {
      if (!previous) {
        return previous;
      }
      const generatedId = `hub_${Date.now()}`;
      const newHub: HubEntity = {
        id: generatedId,
        record: {
          subject_area: "",
          ensemble: "",
          target_schema: "",
          target_table: "",
          target_entity_rdv_name: "",
          target_entity_rdv_id: "",
          datavault_construct_type: getConstructTypeForState("hub", false),
          distribution_type: "",
          distribution_key: "",
          dw_target_types: defaultDwTargetType ?? null,
          exclude_metadata_tests: "",
          include_dq_test: "Y",
          generate_model_entity: "Y",
          dq_source_filter: "",
          Description: "",
          data_source_name: "",
          data_source_id: "",
          source_entity_name: "",
          source_entity_id: "",
          source_filter: ""
        },
        mappings: [],
        columns: []
      };
      createdId = generatedId;
      return {
        ...previous,
        hubs: [...previous.hubs, newHub]
      };
    });
    if (createdId) {
      showStatus("Draft hub added. Populate metadata to finalize.");
    }
    return createdId;
  }, [showStatus]);

  const addSatellite = useCallback((): string | null => {
      if (!model?.hubs.length) {
        showStatus("Add a hub before creating satellites.");
        return null;
      }
      let createdId: string | null = null;
      setModel((previous) => {
        if (!previous) {
          return previous;
        }
        const generatedId = `satellite_${Date.now()}`;
        const newSatellite: SatelliteEntity = {
          id: generatedId,
          record: {
            subject_area: "",
            ensemble: "",
            target_schema: "",
            target_table: "",
            target_entity_rdv_name: "",
            target_entity_rdv_id: "",
            datavault_construct_type: getConstructTypeForState("satellite", false),
            distribution_type: "",
            distribution_key: "",
            dw_target_types: defaultDwTargetType ?? null,
            exclude_metadata_tests: "",
            include_dq_test: "Y",
            generate_model_entity: "Y",
            dq_source_filter: "",
            Description: "",
            data_source_name: "",
            data_source_id: "",
            source_entity_name: "",
            source_entity_id: "",
            source_filter: ""
          },
          parentHubId: null,
          mappings: [],
          columns: []
        };
        createdId = generatedId;
        return {
          ...previous,
          satellites: [...previous.satellites, newSatellite]
        };
      });
      if (createdId) {
        showStatus("Draft satellite added. Map descriptive attributes next.");
      }
      return createdId;
    },
    [model, showStatus]
  );

  const addLink = useCallback(
    (options?: {
      connectedHubIds?: string[];
      targetTable?: string;
      subjectArea?: string;
      ensemble?: string;
      targetSchema?: string;
    }): string | null => {
      if (!model?.hubs.length) {
        showStatus("Add at least one hub before creating links.");
        return null;
      }
      let createdId: string | null = null;
      setModel((previous) => {
        if (!previous) {
          return previous;
        }
        const generatedId = options?.targetTable ?? `link_${Date.now()}_l`;
        const newLink: LinkEntity = {
          id: generatedId,
          record: {
            subject_area: options?.subjectArea ?? "",
            ensemble: options?.ensemble ?? "",
            target_schema: options?.targetSchema ?? "",
            target_table: generatedId,
            target_entity_rdv_name: generatedId,
            target_entity_rdv_id: "",
            datavault_construct_type: getConstructTypeForState("link", false),
            distribution_type: "",
            distribution_key: "",
            dw_target_types: defaultDwTargetType ?? null,
            exclude_metadata_tests: "",
            include_dq_test: "Y",
            generate_model_entity: "Y",
            dq_source_filter: "",
            Description: "",
            data_source_name: "",
            data_source_id: "",
            source_entity_name: "",
            source_entity_id: "",
            source_filter: ""
          },
          connectedHubIds: Array.from(new Set(options?.connectedHubIds ?? [])),
          satelliteIds: [],
          mappings: [],
          columns: [],
          linkedHubs: []
        };
        createdId = generatedId;
        return {
          ...previous,
          links: [...previous.links, newLink]
        };
      });
      if (createdId) {
        showStatus("Draft link added. Connect hubs and satellites to describe the relationship.");
      }
      return createdId;
    },
    [model, showStatus]
  );

  const removeHub = useCallback(
    (hubId: string) => {
      setModel((previous) => {
        if (!previous) {
          return previous;
        }
        const removedHub = previous.hubs.find((hub) => hub.id === hubId);
        const removedHubTargetId = removedHub?.record.target_entity_rdv_id;
        const removedHubTargetName = removedHub?.record.target_table || hubId;
        const satelliteIdsToRemove = new Set(
          previous.satellites
            .filter((satellite) => satellite.parentHubId === hubId)
            .map((satellite) => satellite.id)
        );
        const updatedHubs = previous.hubs.filter((hub) => hub.id !== hubId);
        const updatedSatellites = previous.satellites.filter(
          (satellite) => !satelliteIdsToRemove.has(satellite.id)
        );
        const updatedLinks = previous.links
          .map((link) => {
            const filteredHubIds = link.connectedHubIds.filter((id) => id !== hubId);
            const filteredSatelliteIds = link.satelliteIds.filter(
              (id) => !satelliteIdsToRemove.has(id)
            );
            const filteredLinkedHubs = (link.linkedHubs ?? []).filter((linkedHub) => {
              const matchesId =
                removedHubTargetId &&
                linkedHub.hub_target_entity_rdv_id &&
                String(linkedHub.hub_target_entity_rdv_id) ===
                  String(removedHubTargetId);
              const matchesName =
                linkedHub.hub_target_entity_rdv_name === removedHubTargetName;
              return !(matchesId || matchesName);
            });
            return {
              ...link,
              connectedHubIds: filteredHubIds,
              satelliteIds: filteredSatelliteIds,
              linkedHubs: filteredLinkedHubs
            };
          })
          .filter((link) => link.connectedHubIds.length || link.satelliteIds.length);
        return {
          ...previous,
          hubs: updatedHubs,
          satellites: updatedSatellites,
          links: updatedLinks
        };
      });
      showStatus("Hub removed.");
    },
    [showStatus]
  );

  const removeSatellite = useCallback(
    (satelliteId: string) => {
      setModel((previous) => {
        if (!previous) {
          return previous;
        }
        const updatedSatellites = previous.satellites.filter(
          (satellite) => satellite.id !== satelliteId
        );
        const updatedLinks = previous.links.map((link) => ({
          ...link,
          satelliteIds: link.satelliteIds.filter((id) => id !== satelliteId)
        }));
        return {
          ...previous,
          satellites: updatedSatellites,
          links: updatedLinks
        };
      });
      showStatus("Satellite removed.");
    },
    [showStatus]
  );

  const removeLink = useCallback(
    (linkId: string) => {
      setModel((previous) => {
        if (!previous) {
          return previous;
        }
        return {
          ...previous,
          links: previous.links.filter((link) => link.id !== linkId)
        };
      });
      showStatus("Link removed.");
    },
    [showStatus]
  );

  const removeManual = useCallback(
    (manualId: string) => {
      setModel((previous) => {
        if (!previous) {
          return previous;
        }
        return {
          ...previous,
          manual: previous.manual.filter((entity) => entity.id !== manualId)
        };
      });
      showStatus("Entity removed.");
    },
    [showStatus]
  );

  const addColumnMapping = useCallback(
    (
      entityId: string,
      entityType: "hub" | "satellite" | "link",
      column: ColumnMappingRecord
    ): boolean => {
      let added = false;
      let duplicate = false;
      let missing = false;
      setModel((previous) => {
        if (!previous) {
          return previous;
        }
        if (entityType === "hub") {
          const index = previous.hubs.findIndex((hub) => hub.id === entityId);
          if (index === -1) {
            missing = true;
            return previous;
          }
          const candidate = previous.hubs[index];
          duplicate = candidate.columns.some(
            (existing) =>
              existing.source_schema === column.source_schema &&
              existing.source_table === column.source_table &&
              existing.source_column === column.source_column &&
              existing.target_column === column.target_column
          );
          if (duplicate) {
            return previous;
          }
          added = true;
          const hubs = [...previous.hubs];
          hubs[index] = {
            ...candidate,
            columns: [...candidate.columns, column]
          };
          return {
            ...previous,
            hubs
          };
        }

        if (entityType === "satellite") {
          const index = previous.satellites.findIndex((satellite) => satellite.id === entityId);
          if (index === -1) {
            missing = true;
            return previous;
          }
          const candidate = previous.satellites[index];
          duplicate = candidate.columns.some(
            (existing) =>
              existing.source_schema === column.source_schema &&
              existing.source_table === column.source_table &&
              existing.source_column === column.source_column &&
              existing.target_column === column.target_column
          );
          if (duplicate) {
            return previous;
          }
          added = true;
          const satellites = [...previous.satellites];
          satellites[index] = {
            ...candidate,
            columns: [...candidate.columns, column]
          };
          return {
            ...previous,
            satellites
          };
        }

        const index = previous.links.findIndex((link) => link.id === entityId);
        if (index === -1) {
          missing = true;
          return previous;
        }
        const candidate = previous.links[index];
        duplicate = candidate.columns.some(
          (existing) =>
            existing.source_schema === column.source_schema &&
            existing.source_table === column.source_table &&
            existing.source_column === column.source_column &&
            existing.target_column === column.target_column
        );
        if (duplicate) {
          return previous;
        }
        added = true;
        const links = [...previous.links];
        links[index] = {
          ...candidate,
          columns: [...candidate.columns, column]
        };
        return {
          ...previous,
          links
        };
      });
      if (added) {
        showStatus("Column mapping added.");
        return true;
      }
      if (duplicate) {
        showStatus("This column mapping already exists.");
        return false;
      }
      if (missing) {
        showStatus("Unable to find the selected entity.");
      }
      return false;
    },
    [showStatus]
  );

  const updateHubField = useCallback(
    (hubId: string, field: keyof TableRecord, value: string) => {
      setModel((previous) => {
        if (!previous) {
          return previous;
        }
        const updatedHubs = previous.hubs.map((hub) =>
          hub.id === hubId
            ? {
                ...hub,
                record: {
                  ...hub.record,
                  [field]: value
                }
              }
            : hub
        );
        if (field !== "subject_area" && field !== "ensemble" && field !== "target_schema") {
          return {
            ...previous,
            hubs: updatedHubs
          };
        }
        return {
          ...previous,
          hubs: updatedHubs,
          satellites: previous.satellites.map((satellite) =>
            satellite.parentHubId === hubId
              ? {
                  ...satellite,
                  record: {
                    ...satellite.record,
                    [field]: value
                  }
                }
              : satellite
          )
        };
      });
    },
    []
  );

  const updateManualField = useCallback(
    (manualId: string, field: keyof TableRecord, value: string) => {
      // Manual entities are standalone (not bound to a hub), so no cascade — unlike hubs, whose
      // subject_area/ensemble propagate to their satellites.
      setModel((previous) => {
        if (!previous) {
          return previous;
        }
        return {
          ...previous,
          manual: previous.manual.map((manualEntity) =>
            manualEntity.id === manualId
              ? {
                  ...manualEntity,
                  record: {
                    ...manualEntity.record,
                    [field]: value
                  }
                }
              : manualEntity
          )
        };
      });
    },
    []
  );

  const updateSatelliteField = useCallback(
    (satelliteId: string, field: keyof TableRecord, value: string) => {
      setModel((previous) => {
        if (!previous) {
          return previous;
        }
        return {
          ...previous,
          satellites: previous.satellites.map((satellite) =>
            satellite.id === satelliteId
              ? {
                  ...satellite,
                  record: {
                    ...satellite.record,
                    [field]: value
                  }
                }
              : satellite
          )
        };
      });
    },
    []
  );

  const updateLinkField = useCallback(
    (linkId: string, field: keyof TableRecord, value: string) => {
      setModel((previous) => {
        if (!previous) {
          return previous;
        }
        return {
          ...previous,
          links: previous.links.map((link) =>
            link.id === linkId
              ? {
                  ...link,
                  record: {
                    ...link.record,
                    [field]: value
                  }
                }
              : link
          )
        };
      });
    },
    []
  );

  const updateSatelliteParent = useCallback((satelliteId: string, parentHubId: string | null) => {
    setModel((previous) => {
      if (!previous) {
        return previous;
      }
      const parentHub =
        parentHubId != null ? previous.hubs.find((hub) => hub.id === parentHubId) : null;
      return {
        ...previous,
        satellites: previous.satellites.map((satellite) => {
          if (satellite.id !== satelliteId) {
            return satellite;
          }
          const inheritedRecord =
            parentHub && parentHub.record
              ? {
                  ...satellite.record,
                  subject_area: parentHub.record.subject_area,
                  ensemble: parentHub.record.ensemble,
                  target_schema: parentHub.record.target_schema,
                  data_source_name: parentHub.record.data_source_name,
                  data_source_id: parentHub.record.data_source_id,
                  source_entity_name: parentHub.record.source_entity_name,
                  source_entity_id: parentHub.record.source_entity_id
                }
              : satellite.record;
          return {
            ...satellite,
            parentHubId,
            record: inheritedRecord
          };
        })
      };
    });
  }, []);

  /**
   * Set which hubs a link connects, IN THE ORDER CHOSEN.
   *
   * Order is the point: the first hub gives the link its name, ensemble and subject area. Unlike
   * `updateSatelliteParent`, which overwrites those fields outright, this only moves a field the
   * configurator has not taken over — see `helpers/linkNaming`. Renaming a link somebody
   * deliberately named would be worse than leaving it stale.
   */
  const updateLinkHubs = useCallback((linkId: string, hubIds: string[]) => {
    setModel((previous) => {
      if (!previous) {
        return previous;
      }
      const hubById = new Map(previous.hubs.map((hub) => [hub.id, hub]));
      const nextHubIds = hubIds.filter((id, index) => id && hubIds.indexOf(id) === index);
      const toNamingHub = (id: string) => {
        const hub = hubById.get(id);
        return {
          targetTable: hub?.record.target_table ?? id,
          ensemble: hub?.record.ensemble ?? "",
          subjectArea: hub?.record.subject_area ?? ""
        };
      };
      return {
        ...previous,
        links: previous.links.map((link) => {
          if (link.id !== linkId) {
            return link;
          }
          const naming = nextLinkNaming(
            {
              targetTable: link.record.target_table,
              ensemble: link.record.ensemble,
              subjectArea: link.record.subject_area
            },
            link.connectedHubIds.map(toNamingHub),
            nextHubIds.map(toNamingHub)
          );
          return {
            ...link,
            connectedHubIds: nextHubIds,
            record: {
              ...link.record,
              ...(naming.targetTable
                ? {
                    target_table: naming.targetTable,
                    target_entity_rdv_name: naming.targetTable
                  }
                : {}),
              ...(naming.ensemble ? { ensemble: naming.ensemble } : {}),
              ...(naming.subjectArea ? { subject_area: naming.subjectArea } : {})
            }
          };
        })
      };
    });
  }, []);

  return {
    model,
    setModel,
    addHub,
    addSatellite,
    addLink,
    removeHub,
    removeSatellite,
    removeLink,
    removeManual,
    updateLinkHubs,
    updateHubField,
    updateManualField,
    updateSatelliteField,
    updateLinkField,
    updateSatelliteParent,
    addColumnMapping
  };
}
